import { FC, MouseEvent, useRef, useState } from "react";
import styled from "styled-components";

import useOnClickOutside from "@/hooks/useOnClickOutside";
import Button from "@/components/Button";
import FiltersDropdown from "@/components/FilterDropdown";
import {
  FilterGroupBody,
  FilterGroupHeader,
  FilterGroupTitle,
} from "@/components/FilterDropdown/Parts";
import FilterRadioButton from "@/components/FilterDropdown/FilterRadioButton";

const Holder = styled.div`
  position: relative;
`;

const StyledButton = styled(Button)`
  margin-left: 8px;
  svg {
    margin-right: 3px;
    transform: translateY(-1px);
  }
`;

export enum Sort {
  PUBLISHED = "publishedAt",
  TOTAL_VIEWS = "views",
  V7 = "v7",
  VPH7 = "vph7",
  V7_INDEX = "v7Index",
  R7 = "r7",
}

const renderSortTitle = (sort: Sort) => {
  switch (sort) {
    case Sort.PUBLISHED:
      return "Published";
    case Sort.TOTAL_VIEWS:
      return "Total views";
    case Sort.V7:
      return "V7";
    case Sort.VPH7:
      return "VPH7";
    case Sort.V7_INDEX:
      return "V7 index";
    case Sort.R7:
      return "R7";
    default:
      return "Published";
  }
};

interface Props {
  activeSort: Sort;
  setActiveSort: React.Dispatch<React.SetStateAction<Sort>>;
}

const TopThumbnailsSort: FC<Props> = ({ activeSort, setActiveSort }) => {
  const [showSort, setShowSort] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowSort(false));

  return (
    <Holder ref={ref}>
      <StyledButton
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          setShowSort(!showSort);
        }}
        color="action"
        active
        small
      >
        <svg
          width="20"
          height="18"
          viewBox="0 0 20 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.39258 4.07144C4.39258 3.65723 4.05679 3.32144 3.64258 3.32144C3.22836 3.32144 2.89258 3.65723 2.89258 4.07144V15.1177L2.03033 14.2554C1.73744 13.9625 1.26256 13.9625 0.96967 14.2554C0.676777 14.5483 0.676777 15.0232 0.96967 15.3161L3.10039 17.4468C3.23349 17.586 3.41968 17.6741 3.62646 17.6784C3.65742 17.6791 3.68842 17.6779 3.71926 17.6747C3.75156 17.6714 3.78326 17.6661 3.81421 17.6589C3.94577 17.6281 4.07063 17.5615 4.17319 17.4589L6.31604 15.3161C6.60894 15.0232 6.60894 14.5483 6.31604 14.2554C6.02315 13.9625 5.54828 13.9625 5.25538 14.2554L4.39258 15.1182V4.07144Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.9051 3.57183C11.7677 3.41817 11.568 3.32144 11.3457 3.32144C11.3454 3.32144 11.3451 3.32144 11.3449 3.32144C11.1526 3.32123 10.9604 3.39445 10.8137 3.54111L8.67084 5.68397C8.37795 5.97686 8.37795 6.45174 8.67084 6.74463C8.96373 7.03752 9.43861 7.03752 9.7315 6.74463L10.5957 5.88043V16.9286C10.5957 17.3428 10.9315 17.6786 11.3457 17.6786C11.7599 17.6786 12.0957 17.3428 12.0957 16.9286V5.88378L12.9566 6.74463C13.2494 7.03752 13.7243 7.03752 14.0172 6.74463C14.3101 6.45174 14.3101 5.97686 14.0172 5.68397L11.9051 3.57183Z"
            fill="currentColor"
          />
          <circle cx="16" cy="4" r="3.5" fill="#F64629" stroke="white" />
        </svg>
        Sort
      </StyledButton>
      {showSort && (
        <FiltersDropdown onClose={() => setShowSort(false)}>
          <FilterGroupHeader>
            <FilterGroupTitle>Sort by (Metric)</FilterGroupTitle>
          </FilterGroupHeader>
          <FilterGroupBody>
            {Object.values(Sort).map((sort, index) => (
              <FilterRadioButton
                key={index}
                id={`periodRadio${index}`}
                value={sort}
                checked={activeSort === sort}
                setValue={() => setActiveSort(sort)}
              >
                {renderSortTitle(sort)}
              </FilterRadioButton>
            ))}
          </FilterGroupBody>
        </FiltersDropdown>
      )}
    </Holder>
  );
};

export default TopThumbnailsSort;
